import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserDropdown from './UserDropdown';
import Footer from './Footer';
import EmailVerificationBanner from './EmailVerificationBanner';
import '../styles/navbar.css';
import '../styles/sidebar.css';
import '../styles/design-tokens.css';
import cartService from '../services/cart';
import api from '../config/api';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [cartItemCount, setCartItemCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [settings, setSettings] = useState({
    banner_line_1: 'Order PureHD hotel room equipment here…',
    banner_line_2: 'Set-Top Boxes • Remotes • Cables • Power Supplies',
    support_phone_number: '(888) 787-3431'
  });

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  const handleStartNewOrder = () => {
    // Navigate to products page without any default filters
    navigate('/products');
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Auto-close sidebar on desktop
      if (window.innerWidth >= 992) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch system settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/grouped');
        setSettings({
          banner_line_1: response.data.banner_line_1,
          banner_line_2: response.data.banner_line_2,
          support_phone_number: response.data.support_phone_number
        });
      } catch (error) {
        console.error('Failed to load system settings:', error);
        // Keep default values if fetch fails
      }
    };

    fetchSettings();
  }, []);

  // Refresh user data when window regains focus (e.g., after email verification)
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        checkAuth(); // Refresh user data to get updated email_verified status
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, checkAuth]);

  // Fetch cart item count
  useEffect(() => {
    let abortController = new AbortController();
    
    const fetchCartCount = async () => {
      try {
        const response = await cartService.getCart();
        console.log('Cart API Response:', response); // Debug log
        console.log('Cart items:', response.items); // Debug items
        console.log('First item details:', response.items?.[0]); // Debug first item
        
        // Exclude bundled items from cart count
        const totalItems = response.items?.reduce((sum: number, item: any) => {
          if (item.is_bundled_item) return sum;
          return sum + (item.quantity || 0);
        }, 0) || 0;
        
        // Check multiple possible field names for total
        let totalAmount = 0;
        const resp = response as any; // Type assertion to check various fields
        if (typeof response.total_amount === 'number') {
          totalAmount = response.total_amount;
        } else if (typeof resp.totalAmount === 'number') {
          totalAmount = resp.totalAmount;
        } else if (typeof resp.total === 'number') {
          totalAmount = resp.total;
        } else if (response.items && Array.isArray(response.items)) {
          // Calculate from items if no total field exists
          totalAmount = response.items.reduce((sum: number, item: any) => {
            // Use subtotal if available, otherwise calculate
            if (typeof item.subtotal === 'number') {
              return sum + item.subtotal;
            }
            const price = item.price_at_time || item.price || item.unit_price || 0;
            const qty = item.quantity || 0;
            return sum + (price * qty);
          }, 0);
        }
        
        console.log('Total amount fields check:', {
          total_amount: response.total_amount,
          totalAmount: resp.totalAmount,
          total: resp.total,
          calculated: totalAmount
        }); // Debug all total fields
        setCartItemCount(totalItems);
        setCartTotal(typeof totalAmount === 'number' && !isNaN(totalAmount) ? totalAmount : 0);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching cart:', error);
          // Reset to zero on error
          setCartItemCount(0);
          setCartTotal(0);
        }
      }
    };

    // Listen for cart updates
    const handleCartUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.itemCount !== undefined) {
        // Use the item count provided in the event for instant update
        setCartItemCount(customEvent.detail.itemCount);
        // Still fetch in background to ensure accuracy
        fetchCartCount();
      } else {
        // Fallback to fetching if no count provided
        fetchCartCount();
      }
    };
    window.addEventListener('cartUpdated', handleCartUpdate);

    if (user) {
      fetchCartCount();
      // Refresh cart count every 30 seconds
      const interval = setInterval(fetchCartCount, 30000);
      return () => {
        clearInterval(interval);
        abortController.abort();
        window.removeEventListener('cartUpdated', handleCartUpdate);
      };
    }
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [user]); // Removed location to prevent refetching on navigation

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 992;
  const isDesktop = windowWidth >= 992;

  // Dynamic sidebar width based on screen size
  const sidebarWidth = isDesktop ? 260 : isTablet ? 200 : 260; // Mobile uses full width overlay

  return (
    <div className="layout-wrapper">
      <style>{`
        :root {
          --sidebar-width: ${sidebarWidth}px;
          --sidebar-visible: ${isDesktop ? sidebarWidth : 0}px;
        }

        .layout-wrapper {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          position: relative;
        }

        /* Overlay for mobile sidebar */
        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1049;
          display: ${sidebarOpen && !isDesktop ? 'block' : 'none'};
          transition: opacity 0.3s;
        }

        /* Mobile menu button */
        .mobile-menu-btn {
          display: ${!isDesktop ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 4px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
          margin-right: 10px;
        }

        .mobile-menu-btn:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        /* Responsive sidebar */
        .dashboard-sidebar {
          width: ${sidebarWidth}px !important;
          transform: ${!isDesktop ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)'};
          transition: transform 0.3s ease;
          z-index: ${!isDesktop ? 1050 : 50};
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
        }

        /* Responsive top bar */
        .unified-top-bar {
          left: ${isDesktop ? sidebarWidth : 0}px !important;
          transition: left 0.3s ease;
          padding-left: ${!isDesktop ? '10px' : '15px'} !important;
        }

        /* Responsive main content */
        .dashboard-main-content {
          margin-left: ${isDesktop ? sidebarWidth : 0}px !important;
          width: ${isDesktop ? `calc(100% - ${sidebarWidth}px)` : '100%'} !important;
          transition: margin-left 0.3s ease, width 0.3s ease;
          padding: ${isMobile ? '120px 15px 20px 15px' : '120px 30px 30px 30px'};
        }

        /* Responsive grid for main content */
        @media (min-width: 992px) {
          .dashboard-main-content .row > .col-xl-8,
          .dashboard-main-content .row > .col-lg-7 {
            flex: 0 0 auto;
            width: 66.66667%;
          }
          .dashboard-main-content .row > .col-xl-4,
          .dashboard-main-content .row > .col-lg-5 {
            flex: 0 0 auto;
            width: 33.33333%;
          }
        }

        @media (max-width: 991px) {
          .dashboard-main-content .row > [class*="col-"] {
            flex: 0 0 auto;
            width: 100%;
            max-width: 100%;
          }
        }

        /* Footer responsiveness */
        .portal-footer {
          margin-left: ${isDesktop ? sidebarWidth : 0}px;
          width: ${isDesktop ? `calc(100% - ${sidebarWidth}px)` : '100%'};
          transition: margin-left 0.3s ease, width 0.3s ease;
        }
      `}</style>
      {/* Mobile sidebar overlay */}
      <div 
        className="sidebar-overlay" 
        onClick={() => setSidebarOpen(false)}
      />

      {/* Unified Top Bar */}
      <div className="unified-top-bar" style={{
        background: '#4D64C1',  // Match sidebar purple exactly
        height: '100px',  // Fixed height to match sidebar logo
        display: 'flex',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        right: 0,
        zIndex: 1040,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '0 15px'
      }}>
        <div className="container-fluid" style={{ padding: 0 }}>
          <div className="d-flex align-items-center" style={{ height: '100%', position: 'relative' }}>
            {/* Left Section - Menu button only */}
            <div style={{
              position: 'absolute',
              left: 0,
              display: 'flex',
              alignItems: 'center'
            }}>
              {/* Mobile menu button */}
              <button
                className="mobile-menu-btn"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle menu"
                style={{ flexShrink: 0 }}
              >
                <i className="fas fa-bars" style={{ fontSize: '18px' }}></i>
              </button>
            </div>

            {/* Center Section - Equipment Info & Support */}
            <div style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <div style={{
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.3px',
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}>
                {settings.banner_line_1}
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: '11px',
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}>
                {settings.banner_line_2}
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.95)',
                fontSize: '12px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}>
                <i className="fas fa-phone-alt" style={{ fontSize: '11px' }}></i>
                <span>Support: {settings.support_phone_number}</span>
              </div>
            </div>

            {/* Right Section - User Menu & Cart */}
            <div className="d-flex align-items-center" style={{
              position: 'absolute',
              right: 0,
              gap: '20px'
            }}>
              <UserDropdown />
              <Link
                to="/cart"
                className="cart-display"
                style={{
                  color: 'white',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '6px 12px',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
                }}
              >
                <div style={{ position: 'relative' }}>
                  <i className="fas fa-shopping-cart" style={{ fontSize: '20px' }}></i>
                  {cartItemCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      backgroundColor: 'var(--color-danger, #B71C1C)',
                      color: 'white',
                      borderRadius: '50%',
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      minWidth: '18px',
                      textAlign: 'center',
                      border: '2px solid var(--color-primary, #2E5090)'
                    }}>{cartItemCount}</span>
                  )}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '10px', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cart</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>
                    {cartItemCount > 0 && !isNaN(cartTotal) ? `$${cartTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout with Sidebar */}
      <div className="dashboard-with-sidebar">
          {/* Mobile overlay */}
          {sidebarOpen && (
            <div 
              className="sidebar-overlay d-lg-none"
              onClick={() => setSidebarOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1039
              }}
            />
          )}
          
          {/* Sidebar */}
          <aside className={`dashboard-sidebar ${sidebarOpen ? 'mobile-open' : ''}`}>
          {/* Logo */}
          <div className="sidebar-logo" style={{
            background: '#4D64C1',  // Same purple as navbar
            padding: '20px',
            borderBottom: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100px',  // Same height as navbar
            position: 'relative',
            zIndex: 1053
          }}>
            <a href="https://www.purehd.com" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {!logoError ? (
                <img 
                  src="/img/PureHD_Logo.png" 
                  alt="PureHD Logo" 
                  style={{ 
                    height: '55px',
                    width: 'auto',
                    display: 'block',
                    filter: 'brightness(0) invert(1)',
                    objectFit: 'contain'
                  }}
                  onLoad={() => console.log('Logo loaded successfully!')}
                  onError={() => {
                    console.error('Logo failed to load from /img/PureHD_Logo.png');
                    setLogoError(true);
                  }}
                />
              ) : (
                <span style={{
                  color: 'white',
                  fontSize: '32px',
                  fontWeight: 700,
                  letterSpacing: '-1px'
                }}>PureHD</span>
              )}
            </a>
          </div>
          
          {/* Main Navigation */}
          <div className="sidebar-section">
            <ul className="sidebar-nav">
              <li className="sidebar-nav-item">
                <Link to="/" className={`sidebar-nav-link ${isActive('/')}`}>
                  <i className="fas fa-tachometer-alt"></i>
                  <span>Dashboard</span>
                </Link>
              </li>
              <li className="sidebar-nav-item">
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleStartNewOrder(); }}
                  className="sidebar-nav-link new-order"
                  style={{
                    background: 'var(--color-success, #2E7D32)',
                    color: 'white',
                    fontWeight: '600',
                    border: '1px solid rgba(255,255,255,0.2)',
                    margin: '8px'
                  }}
                >
                  <i className="fas fa-box"></i>
                  <span>{cartItemCount > 0 ? 'Continue Shopping' : 'Start New Order'}</span>
                </a>
              </li>
            </ul>
          </div>
          
          {/* Order Management */}
          <div className="sidebar-section">
            <div className="sidebar-section-title">Order Management</div>
            <ul className="sidebar-nav">
              <li className="sidebar-nav-item">
                <Link to="/cart" className={`sidebar-nav-link ${isActive('/cart')}`}>
                  <i className="fas fa-shopping-cart"></i>
                  <span>Shopping Cart</span>
                  {cartItemCount > 0 && (
                    <span className="badge bg-danger ms-auto">{cartItemCount}</span>
                  )}
                </Link>
              </li>
              <li className="sidebar-nav-item">
                <Link to="/orders" className={`sidebar-nav-link ${isActive('/orders')}`}>
                  <i className="fas fa-box-open"></i>
                  <span>Orders</span>
                </Link>
              </li>
              <li className="sidebar-nav-item">
                <Link to="/drafts" className={`sidebar-nav-link ${isActive('/drafts')}`}>
                  <i className="fas fa-file-invoice"></i>
                  <span>Drafts</span>
                </Link>
              </li>
            </ul>
          </div>
        </aside>

        {/* Mobile Sidebar Toggle */}
        <button 
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <i className={`fas fa-${sidebarOpen ? 'times' : 'bars'}`}></i>
        </button>

          {/* Email Verification Banner */}
          <EmailVerificationBanner />

          {/* Main Content */}
          <div className={`dashboard-main-content ${location.pathname === '/' ? 'dashboard-page' : ''}`}>
            {children}
          </div>
        </div>

        <Footer />
    </div>
  );
};

export default Layout; 